import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, MapPinLine, Clock, Car, Users, Suitcase, CheckCircle, Spinner, Truck,
} from '@phosphor-icons/react';
import logisticsApi from '../../lib/logisticsApi';

const fmt = (minor) =>
  ((minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABELS = {
  sedan: 'Car', suv: 'SUV', executive: 'Executive', van: 'Van',
  minibus: 'Minibus', coach: 'Coach',
  rail_standard: 'Standard Class', rail_first: 'First Class',
  bus: 'Bus', express_coach: 'Express Coach',
  ferry_foot: 'Foot Passenger', ferry_car: 'Car + Driver', ferry_van: 'Van + Driver',
};

const DepotDetailPanel = ({
  open, onClose, exerciseId, venue, currencySymbol = 'Ÿ',
  onSelectVehicle, bookDisabledReason, routeInfo,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (!open || !exerciseId || !venue?.venue_id) { setDetail(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await logisticsApi.transportVenueDetail(exerciseId, venue.venue_id);
        if (!cancelled) setDetail(d);
      } catch { if (!cancelled) setDetail(null); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, exerciseId, venue?.venue_id]);

  const subcategoryLabel = (detail?.subcategory || '').replace(/_/g, ' ');

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }} onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
          />
          <motion.aside
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="ls-glass fixed top-0 right-0 h-full z-50 flex flex-col w-full sm:w-[540px]"
            style={{ borderLeft: '1px solid var(--ls-border)' }}
            data-testid="ls-depot-detail"
            role="dialog" aria-label="Transport depot detail"
          >
            <div className="flex items-center justify-between px-5 h-14 shrink-0" style={{ borderBottom: '1px solid var(--ls-border)' }}>
              <div className="flex items-center gap-2">
                <Truck size={16} weight="fill" style={{ color: 'var(--ls-primary)' }} />
                <span className="ls-heading text-sm font-bold">Transport Depot</span>
              </div>
              <button onClick={onClose} className="ls-btn-ghost !p-2" aria-label="Close">
                <X size={14} weight="bold" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Spinner size={24} className="animate-spin" style={{ color: 'var(--ls-primary)' }} />
                </div>
              ) : detail ? (
                <>
                  {/* Gallery */}
                  {detail.images?.length > 0 && (
                    <div className="relative">
                      <div className="w-full h-56 bg-cover bg-center" style={{ backgroundImage: `url(${detail.images[activeImage]})` }} />
                      {detail.images.length > 1 && (
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
                          {detail.images.map((_, i) => (
                            <button key={i} onClick={() => setActiveImage(i)}
                              className="w-2 h-2 rounded-full transition-all"
                              style={{ background: i === activeImage ? 'var(--ls-primary)' : 'rgba(255,255,255,0.5)', transform: i === activeImage ? 'scale(1.3)' : 'scale(1)' }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info */}
                  <div className="px-5 py-4">
                    <p className="ls-overline mb-1">{subcategoryLabel}</p>
                    <h2 className="ls-heading text-xl font-bold">{detail.name}</h2>
                    {detail.address && (
                      <p className="text-xs flex items-center gap-1 mt-1" style={{ color: 'var(--ls-muted)' }}>
                        <MapPinLine size={12} weight="fill" /> {detail.address}
                      </p>
                    )}
                    {detail.description && (
                      <p className="text-sm mt-3" style={{ color: 'var(--ls-secondary)' }}>{detail.description}</p>
                    )}
                    {detail.info && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        <span className="text-xs flex items-center gap-1 ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>
                          <Clock size={12} weight="bold" style={{ color: 'var(--ls-primary)' }} />
                          {detail.info.twenty_four_hour ? '24 hours' : `${detail.info.opens} – ${detail.info.closes}`}
                        </span>
                        {detail.info.one_way_available && (
                          <span className="text-xs ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>One-way available</span>
                        )}
                        {detail.info.chauffeur_available && (
                          <span className="text-xs ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>Chauffeur</span>
                        )}
                        {detail.info.ticket_office && (
                          <span className="text-xs ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>Ticket Office</span>
                        )}
                        {detail.info.duty_free && (
                          <span className="text-xs ls-elevated px-2 py-1 rounded-full" style={{ border: '1px solid var(--ls-border)' }}>Duty Free</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Route summary if available */}
                  {routeInfo && (
                    <div className="mx-5 mb-4 p-3 rounded-lg" style={{ background: 'var(--ls-elevated)', border: '1px solid var(--ls-border)' }}>
                      <p className="ls-overline mb-1">Your route</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium" style={{ color: 'var(--ls-secondary)' }}>
                          {routeInfo.originName || 'Origin'} → {routeInfo.destinationName || 'Destination'}
                        </span>
                      </div>
                      <div className="flex gap-4 mt-1">
                        <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>
                          {routeInfo.estimated_road_km} km
                        </span>
                        <span className="text-xs" style={{ color: 'var(--ls-muted)' }}>
                          ~{routeInfo.estimated_duration_minutes} min
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Fleet */}
                  <div className="px-5 pb-5">
                    <h3 className="ls-heading text-sm font-bold mb-3" style={{ borderBottom: '1px solid var(--ls-border)', paddingBottom: 8 }}>
                      Available Vehicles
                    </h3>
                    <div className="space-y-3">
                      {detail.fleet?.map((v) => (
                        <div
                          key={v.vehicle_id}
                          className="flex gap-3 p-3 rounded-lg transition-all cursor-pointer group"
                          style={{ border: '1px solid var(--ls-border)', background: 'var(--ls-surface)' }}
                          onClick={() => !bookDisabledReason && onSelectVehicle?.(venue, v)}
                          data-testid={`ls-vehicle-${v.vehicle_id}`}
                        >
                          <div
                            className="w-24 h-20 rounded-md flex-shrink-0 bg-cover bg-center"
                            style={{ backgroundImage: `url(${v.photo})`, border: '1px solid var(--ls-border)' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs px-1.5 py-0.5 rounded font-semibold"
                                style={{ background: 'var(--ls-elevated)', color: 'var(--ls-primary)', border: '1px solid var(--ls-border)', fontSize: 10 }}>
                                {TYPE_LABELS[v.type] || v.type}
                              </span>
                              <span className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>
                                {v.available_units} available
                              </span>
                            </div>
                            <p className="text-sm font-semibold" style={{ color: 'var(--ls-secondary)' }}>{v.name}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-[11px] flex items-center gap-0.5" style={{ color: 'var(--ls-muted)' }}>
                                <Users size={10} weight="bold" /> {v.capacity}
                              </span>
                              <span className="text-[11px] flex items-center gap-0.5" style={{ color: 'var(--ls-muted)' }}>
                                <Suitcase size={10} weight="bold" /> {v.luggage}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {v.features.slice(0, 4).map((f) => (
                                <span key={f} className="text-[9px] px-1.5 py-0.5 rounded-full"
                                  style={{ background: 'var(--ls-elevated)', color: 'var(--ls-muted)', border: '1px solid var(--ls-border)' }}>
                                  {f}
                                </span>
                              ))}
                              {v.features.length > 4 && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ color: 'var(--ls-muted)' }}>
                                  +{v.features.length - 4}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end justify-between flex-shrink-0">
                            <div className="text-right">
                              <p className="ls-mono text-sm font-bold" style={{ color: 'var(--ls-primary)' }}>
                                {currencySymbol}{fmt(v.price_per_km_minor)}
                              </p>
                              <p className="text-[10px]" style={{ color: 'var(--ls-muted)' }}>/km</p>
                            </div>
                            {v.price_per_hour_minor > 0 && (
                              <div className="text-right">
                                <p className="ls-mono text-xs" style={{ color: 'var(--ls-muted)' }}>
                                  {currencySymbol}{fmt(v.price_per_hour_minor)}/hr
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {bookDisabledReason && (
                      <p className="text-[11px] text-center mt-3" style={{ color: 'var(--ls-danger)' }}>{bookDisabledReason}</p>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center py-20">
                  <p className="text-sm" style={{ color: 'var(--ls-muted)' }}>Could not load details</p>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default DepotDetailPanel;
